import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Typography, TextField, Button, MenuItem, Select, InputLabel, FormControl, Box,
  IconButton, Grid, FormGroup, FormControlLabel, Checkbox, Chip, Paper, Divider, Alert,
  Tabs, Tab, Radio, RadioGroup
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import dayjs from 'dayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import 'dayjs/locale/el';

// Constants
const DAYS_OF_WEEK = [
  { value: 1, label: 'Δευτέρα' },
  { value: 2, label: 'Τρίτη' },
  { value: 3, label: 'Τετάρτη' },
  { value: 4, label: 'Πέμπτη' },
  { value: 5, label: 'Παρασκευή' },
  { value: 6, label: 'Σάββατο' },
  { value: 0, label: 'Κυριακή' }
];

const DAYS_FOR_SCHEDULE = [
  { value: 1, label: 'Δευτέρα' },
  { value: 2, label: 'Τρίτη' },
  { value: 3, label: 'Τετάρτη' },
  { value: 4, label: 'Πέμπτη' },
  { value: 5, label: 'Παρασκευή' },
  { value: 6, label: 'Σάββατο' },
  { value: 0, label: 'Κυριακή' }
];

/**
 * Shared Employee Management Modal Component
 * 
 * Props:
 * - open: boolean - Whether the modal is open
 * - employee: object - The employee being managed
 * - tab: number - Current active tab
 * - onClose: function - Called when modal should close
 * - onTabChange: function(newTab) - Called when tab changes
 * - positions: array - List of available positions
 * - presetHours: array - List of preset hours (optional, for fixed schedule)
 * 
 * Form Props:
 * - editForm: object - Current form values
 * - onEditFormChange: function(event) - Called when form field changes
 * - onEditFormSet: function(newForm) - Called to set entire form
 * - onEditSave: function - Called to save basic info
 * - onPreferencesSave: function - Called to save preferences
 * 
 * Payment Props:
 * - paymentStats: object - { totalSalary, totalPaid, unpaid }
 * - paymentHistory: array - List of payments
 * - employeeShifts: array - List of shifts for salary calculation
 * - onOpenNewPayment: function - Called to open new payment dialog
 * - onOpenEditPayment: function(payment) - Called to edit a payment
 * - onDeletePayment: function(paymentId) - Called to delete a payment
 * 
 * Fixed Schedule Props (optional):
 * - showFixedSchedule: boolean - Whether to show fixed schedule tab
 * - fixedSchedule: object - Fixed schedule data
 * - onFixedScheduleChange: function(newSchedule) - Called when schedule changes
 * - onSaveFixedSchedule: function - Called to save fixed schedule
 * - savingFixedSchedule: boolean - Whether fixed schedule is being saved
 */
export default function EmployeeManagementModal({
  // Basic props
  open,
  employee,
  tab,
  onClose,
  onTabChange,
  positions = [],
  presetHours = [],
  
  // Form props
  editForm,
  onEditFormChange,
  onEditFormSet,
  onEditSave,
  onPreferencesSave,
  
  // Payment props
  paymentStats = { totalSalary: 0, totalPaid: 0, unpaid: 0 },
  paymentHistory = [],
  employeeShifts = [],
  onOpenNewPayment,
  onOpenEditPayment,
  onDeletePayment,
  
  // Fixed schedule props (optional)
  showFixedSchedule = false,
  fixedSchedule = { enabled: false, days: {} },
  onFixedScheduleChange,
  onSaveFixedSchedule,
  savingFixedSchedule = false,
  
  // Refetch functions (optional)
  onRefetchPaymentHistory,
  onRefetchShifts
}) {
  // Local state for history filters - NO DEFAULT FILTERS (show ALL TIME by default)
  const [historyDateFrom, setHistoryDateFrom] = useState(null);
  const [historyDateTo, setHistoryDateTo] = useState(null);
  const [historyGroupBy, setHistoryGroupBy] = useState('none');

  // Reset filters when modal opens (clear filters to show ALL TIME)
  useEffect(() => {
    if (open) {
      setHistoryDateFrom(null);
      setHistoryDateTo(null);
      setHistoryGroupBy('none');
    }
  }, [open]);

  // Calculate payment stats from shifts with proper hourly_wage handling
  const calculatedPaymentStats = useMemo(() => {
    // Default stats structure
    const defaultStats = { totalSalary: 0, totalPaid: 0, unpaid: 0 };
    
    if (!employee || !employeeShifts || employeeShifts.length === 0) {
      // Merge paymentStats with defaults to ensure all fields exist
      return { ...defaultStats, ...paymentStats };
    }

    // Collect all week_start values from shifts to know which weeks have work
    const shiftsWeekStarts = new Set(employeeShifts.map(s => s.week_start));
    

    let totalSalary = 0;
    employeeShifts.forEach(shift => {
      const startParts = shift.start_time.split(':');
      const endParts = shift.end_time.split(':');
      const startMinutes = parseInt(startParts[0]) * 60 + parseInt(startParts[1]);
      let endMinutes = parseInt(endParts[0]) * 60 + parseInt(endParts[1]);
      
      // Handle shifts that cross midnight
      if (endMinutes < startMinutes) {
        endMinutes += 24 * 60;
      }
      const hours = (endMinutes - startMinutes) / 60;
      
      if (hours > 0) {
        // USE STORED WAGE FROM SHIFT FOR HISTORICAL ACCURACY
        // Fallback to employee wage for older shifts that don't have hourly_wage stored
        let wageRate = shift.hourly_wage;
        
        if (wageRate === null || wageRate === undefined) {
          // For old shifts without stored wage, determine based on day
          const empData = shift.employee || employee;
          if (empData) {
            // day: 0=Sunday, 6=Saturday
            if (shift.day === 0) {
              wageRate = empData.sunday_wage || empData.hourly_wage || 0;
            } else if (shift.day === 6) {
              wageRate = empData.saturday_wage || empData.hourly_wage || 0;
            } else {
              wageRate = empData.hourly_wage || 0;
            }
          } else {
            wageRate = 0;
          }
        }

        totalSalary += hours * wageRate;
      }
    });

    // Calculate totalPaid using FIFO allocations - only count payments for weeks that have shifts
    let totalPaid = 0;
    paymentHistory.forEach(payment => {
      const allocations = payment.allocations || [];
      
      if (allocations.length > 0) {
        // Use allocations to determine which weeks this payment covers
        allocations.forEach(alloc => {
          // Only count allocation if the week_start exists in our shifts
          if (alloc.week_start && shiftsWeekStarts.has(alloc.week_start)) {
            totalPaid += parseFloat(alloc.allocated_amount || 0);
          }
        });
      } else {
        // Fallback for old payments without allocations
        // Try to match by payment_date falling within a shift week
        const paymentDate = payment.payment_date?.split('T')[0];
        if (paymentDate) {
          // Check if payment date falls within any shift week
          for (const weekStart of shiftsWeekStarts) {
            const weekEnd = dayjs(weekStart).add(6, 'day').format('YYYY-MM-DD');
            if (paymentDate >= weekStart && paymentDate <= weekEnd) {
              totalPaid += parseFloat(payment.amount || 0);
              break; // Only count once
            }
          }
        }
      }
    });

    const unpaid = totalSalary - totalPaid;

    return {
      totalSalary: totalSalary || 0,
      totalPaid: totalPaid || 0,
      unpaid: unpaid || 0
    };
  }, [employee, employeeShifts, paymentHistory, paymentStats]);

  // Helper to convert dayjs to string format for comparison
  const formatDateForCompare = (date) => {
    if (!date) return null;
    return dayjs.isDayjs(date) ? date.format('YYYY-MM-DD') : date;
  };

  // Helper to check if a week overlaps with a date range
  const weekOverlapsDateRange = (weekStart, dateFrom, dateTo) => {
    if (!weekStart) return false;
    const weekStartDate = weekStart.split('T')[0];
    const weekEndDate = dayjs(weekStartDate).add(6, 'day').format('YYYY-MM-DD');
    const dateFromStr = formatDateForCompare(dateFrom);
    const dateToStr = formatDateForCompare(dateTo);
    
    // Check if the week [weekStart, weekEnd] overlaps with [dateFrom, dateTo]
    if (dateFromStr && weekEndDate < dateFromStr) return false;
    if (dateToStr && weekStartDate > dateToStr) return false;
    return true;
  };

  // Calculate filtered stats based on date range (using FIFO allocations)
  const calculateFilteredStats = () => {
    if (!historyDateFrom && !historyDateTo) {
      return null; // No filtering
    }

    const dateFromStr = formatDateForCompare(historyDateFrom);
    const dateToStr = formatDateForCompare(historyDateTo);

    let filteredShifts = [...employeeShifts];

    // Filter shifts by date
    if (dateFromStr || dateToStr) {
      filteredShifts = filteredShifts.filter(shift => {
        const shiftDate = getShiftDate(shift.week_start, shift.day);
        if (dateFromStr && shiftDate < dateFromStr) return false;
        if (dateToStr && shiftDate > dateToStr) return false;
        return true;
      });
    }

    // Calculate totals from filtered data
    let totalSalary = 0;
    filteredShifts.forEach(shift => {
      const startParts = shift.start_time.split(':');
      const endParts = shift.end_time.split(':');
      const startMinutes = parseInt(startParts[0]) * 60 + parseInt(startParts[1]);
      let endMinutes = parseInt(endParts[0]) * 60 + parseInt(endParts[1]);
      if (endMinutes < startMinutes) endMinutes += 24 * 60;
      const hours = (endMinutes - startMinutes) / 60;

      if (hours > 0) {
        let wageRate = shift.hourly_wage;
        if (wageRate === null || wageRate === undefined) {
          const empData = shift.employee || employee;
          if (empData) {
            if (shift.day === 0) {
              wageRate = empData.sunday_wage || empData.hourly_wage || 0;
            } else if (shift.day === 6) {
              wageRate = empData.saturday_wage || empData.hourly_wage || 0;
            } else {
              wageRate = empData.hourly_wage || 0;
            }
          } else {
            wageRate = 0;
          }
        }
        totalSalary += hours * wageRate;
      }
    });

    // Calculate totalPaid using FIFO allocations instead of payment_date
    // This ensures that a payment made on Jan 22 for week Jan 13-19 shows as paid in Jan 13-19
    let totalPaid = 0;
    paymentHistory.forEach(payment => {
      const allocations = payment.allocations || [];
      
      if (allocations.length > 0) {
        // Use allocations to determine which weeks this payment covers
        allocations.forEach(alloc => {
          if (weekOverlapsDateRange(alloc.week_start, historyDateFrom, historyDateTo)) {
            totalPaid += parseFloat(alloc.allocated_amount || 0);
          }
        });
      } else {
        // Fallback for old payments without allocations - use payment_date
        const paymentDate = payment.payment_date?.split('T')[0];
        if (paymentDate) {
          const inRange = (!dateFromStr || paymentDate >= dateFromStr) && 
                         (!dateToStr || paymentDate <= dateToStr);
          if (inRange) {
            totalPaid += parseFloat(payment.amount || 0);
          }
        }
      }
    });

    const unpaid = totalSalary - totalPaid;

    return { totalSalary, totalPaid, unpaid };
  };

  // Helper to get shift date
  const getShiftDate = (weekStart, day) => {
    const d = dayjs(weekStart);
    // day is 0-6 (Sun-Sat), but our week starts on Monday
    const daysToAdd = day === 0 ? 6 : day - 1;
    return d.add(daysToAdd, 'day').format('YYYY-MM-DD');
  };

  // Get filtered and grouped payment history (using FIFO allocations)
  const getFilteredAndGroupedHistory = () => {
    let filtered = [...paymentHistory];
    const dateFromStr = formatDateForCompare(historyDateFrom);
    const dateToStr = formatDateForCompare(historyDateTo);

    // Apply date filters using allocations (FIFO) instead of payment_date
    if (dateFromStr || dateToStr) {
      filtered = filtered.filter(payment => {
        const allocations = payment.allocations || [];
        
        if (allocations.length > 0) {
          // Check if ANY allocation falls within the date range
          return allocations.some(alloc => 
            weekOverlapsDateRange(alloc.week_start, historyDateFrom, historyDateTo)
          );
        } else {
          // Fallback for old payments without allocations - use payment_date
          const paymentDate = payment.payment_date?.split('T')[0];
          if (!paymentDate) return false;
          if (dateFromStr && paymentDate < dateFromStr) return false;
          if (dateToStr && paymentDate > dateToStr) return false;
          return true;
        }
      });
    }

    // Sort by date descending
    filtered.sort((a, b) => new Date(b.payment_date) - new Date(a.payment_date));

    // Group if needed
    if (historyGroupBy === 'none') {
      return [{ groupKey: 'all', groupLabel: null, items: filtered, totalAmount: 0 }];
    }

    const groups = {};
    filtered.forEach(payment => {
      const date = dayjs(payment.payment_date);
      let groupKey, groupLabel;

      switch (historyGroupBy) {
        case 'day':
          groupKey = date.format('YYYY-MM-DD');
          groupLabel = date.format('DD/MM/YYYY');
          break;
        case 'week':
          groupKey = date.startOf('isoWeek').format('YYYY-MM-DD');
          groupLabel = `Εβδομάδα ${date.startOf('isoWeek').format('DD/MM')} - ${date.endOf('isoWeek').format('DD/MM/YYYY')}`;
          break;
        case 'month':
          groupKey = date.format('YYYY-MM');
          groupLabel = date.format('MMMM YYYY');
          break;
        default:
          groupKey = 'all';
          groupLabel = null;
      }

      if (!groups[groupKey]) {
        groups[groupKey] = { groupKey, groupLabel, items: [], totalAmount: 0 };
      }
      groups[groupKey].items.push(payment);
      groups[groupKey].totalAmount += parseFloat(payment.amount || 0);
    });

    return Object.values(groups).sort((a, b) => b.groupKey.localeCompare(a.groupKey));
  };

  // Handle tab change with data refetching
  const handleTabChange = (e, newValue) => {
    onTabChange(newValue);
    
    // Refetch data when switching to payment history tab
    if (newValue === 2 && employee) {
      if (onRefetchPaymentHistory) onRefetchPaymentHistory();
      if (onRefetchShifts) onRefetchShifts();
    }
    
    // Fixed schedule tab handling is done in parent if needed
  };

  // Handle modal close
  const handleClose = () => {
    setHistoryDateFrom('');
    setHistoryDateTo('');
    setHistoryGroupBy('none');
    onClose();
  };

  // Get the stats to display (filtered or calculated)
  const getDisplayStats = () => {
    const filteredStats = calculateFilteredStats();
    return filteredStats || calculatedPaymentStats;
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Διαχείριση Υπαλλήλου
        {employee && (
          <Typography variant="body2" color="textSecondary">
            {employee.name} - {employee.position}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs
            value={tab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="Βασικά Στοιχεία" />
            <Tab label="Προτιμήσεις" />
            <Tab label="Ιστορικό Πληρωμών" />
            {showFixedSchedule && <Tab label="Σταθερό Πρόγραμμα" />}
          </Tabs>
        </Box>

        {/* Tab 0: Βασικά Στοιχεία */}
        {tab === 0 && (
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Ονοματεπώνυμο"
              name="name"
              value={editForm.name || ''}
              onChange={onEditFormChange}
              fullWidth
            />
            <TextField
              label="Email"
              name="email"
              type="email"
              value={editForm.email || ''}
              onChange={onEditFormChange}
              fullWidth
              placeholder="employee@email.com"
              helperText="Για αποστολή ειδοποιήσεων βαρδιών"
            />
            <FormControl fullWidth>
              <InputLabel>Πόστο</InputLabel>
              <Select
                name="position"
                value={editForm.position || ''}
                label="Πόστο"
                onChange={onEditFormChange}
              >
                {positions && positions.map(pos => (
                  <MenuItem key={pos} value={pos}>{pos}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Αμοιβή ανά ώρα (Δευτέρα-Παρασκευή)"
              name="hourly_wage"
              type="number"
              value={editForm.hourly_wage || ''}
              onChange={onEditFormChange}
              fullWidth
              inputProps={{ step: "0.01", min: "0" }}
            />
            <TextField
              label="Αμοιβή Σαββάτου"
              name="saturday_wage"
              type="number"
              value={editForm.saturday_wage || ''}
              onChange={onEditFormChange}
              fullWidth
              inputProps={{ step: "0.01", min: "0" }}
              helperText="Αφήστε κενό για την ίδια αμοιβή με καθημερινές"
            />
            <TextField
              label="Αμοιβή Κυριακής"
              name="sunday_wage"
              type="number"
              value={editForm.sunday_wage || ''}
              onChange={onEditFormChange}
              fullWidth
              inputProps={{ step: "0.01", min: "0" }}
              helperText="Αφήστε κενό για την ίδια αμοιβή με καθημερινές"
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
              <Button onClick={handleClose}>Άκυρο</Button>
              <Button onClick={onEditSave} variant="contained">Αποθήκευση</Button>
            </Box>
          </Box>
        )}

        {/* Tab 1: Προτιμήσεις */}
        {tab === 1 && (
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box>
              <Typography variant="h6" gutterBottom>Μέγιστες Ημέρες Εργασίας</Typography>
              <TextField
                label="Μέγιστες ημέρες ανά εβδομάδα"
                name="max_days_per_week"
                type="number"
                value={editForm.max_days_per_week || 7}
                onChange={onEditFormChange}
                inputProps={{ min: 1, max: 7 }}
                fullWidth
              />
            </Box>
            <Divider />
            <Box>
              <Typography variant="h6" gutterBottom>Διαθέσιμες Ημέρες</Typography>
              <FormGroup row>
                {DAYS_OF_WEEK.map((day) => {
                  const isChecked = editForm.available_days
                    ? editForm.available_days.split(',').map(Number).includes(day.value)
                    : true;
                  return (
                    <FormControlLabel
                      key={day.value}
                      control={
                        <Checkbox
                          checked={isChecked}
                          onChange={(e) => {
                            const currentDays = editForm.available_days ? editForm.available_days.split(',').map(Number) : [];
                            const newDays = e.target.checked
                              ? [...currentDays, day.value]
                              : currentDays.filter(d => d !== day.value);
                            onEditFormSet({ ...editForm, available_days: newDays.sort().join(',') });
                          }}
                        />
                      }
                      label={day.label}
                    />
                  );
                })}
              </FormGroup>
            </Box>
            <Divider />
            <Box>
              <Typography variant="h6" gutterBottom>Προτίμηση Βάρδιας</Typography>
              <RadioGroup
                value={editForm.shift_preferences || 'any'}
                onChange={(e) => onEditFormSet({ ...editForm, shift_preferences: e.target.value })}
              >
                <FormControlLabel value="any" control={<Radio />} label="Οποιαδήποτε βάρδια" />
                <FormControlLabel value="morning" control={<Radio />} label="Προτίμηση πρωινές βάρδιες" />
                <FormControlLabel value="evening" control={<Radio />} label="Προτίμηση απογευματινές βάρδιες" />
                <FormControlLabel value="night" control={<Radio />} label="Προτίμηση νυχτερινές βάρδιες" />
              </RadioGroup>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
              <Button onClick={handleClose}>Άκυρο</Button>
              <Button onClick={onPreferencesSave} variant="contained">Αποθήκευση</Button>
            </Box>
          </Box>
        )}

        {/* Tab 2: Ιστορικό Πληρωμών */}
        {tab === 2 && (
          <Box sx={{ pt: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Ιστορικό Πληρωμών</Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={onOpenNewPayment}
              >
                Νέα Πληρωμή
              </Button>
            </Box>

            {/* History Filters */}
            <Paper sx={{ p: 2, bgcolor: '#f5f5f5', mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                Φίλτρα Ιστορικού
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="el">
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={4}>
                    <DatePicker
                      label="Από"
                      value={historyDateFrom}
                      onChange={(newDate) => setHistoryDateFrom(newDate)}
                      format="DD/MM/YYYY"
                      slotProps={{ 
                        textField: { 
                          fullWidth: true, 
                          size: 'small' 
                        } 
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <DatePicker
                      label="Έως"
                      value={historyDateTo}
                      onChange={(newDate) => setHistoryDateTo(newDate)}
                      format="DD/MM/YYYY"
                      slotProps={{ 
                        textField: { 
                          fullWidth: true, 
                          size: 'small' 
                        } 
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      select
                      label="Ομαδοποίηση"
                      fullWidth
                      size="small"
                      value={historyGroupBy}
                      onChange={(e) => setHistoryGroupBy(e.target.value)}
                    >
                      <MenuItem value="none">Καμία</MenuItem>
                      <MenuItem value="day">Ανά Ημέρα</MenuItem>
                      <MenuItem value="week">Ανά Εβδομάδα</MenuItem>
                      <MenuItem value="month">Ανά Μήνα</MenuItem>
                    </TextField>
                  </Grid>
                </Grid>
              </LocalizationProvider>
              {historyGroupBy !== 'none' && (
                <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    size="small"
                    onClick={() => {
                      setHistoryDateFrom(null);
                      setHistoryDateTo(null);
                      setHistoryGroupBy('none');
                    }}
                  >
                    Καθαρισμός Φίλτρων
                  </Button>
                </Box>
              )}
            </Paper>

            {/* Statistics */}
            {(() => {
              const filteredStats = calculateFilteredStats();
              const rawStats = filteredStats || calculatedPaymentStats;
              // Ensure all stats have default values
              const stats = {
                totalSalary: rawStats?.totalSalary ?? 0,
                totalPaid: rawStats?.totalPaid ?? 0,
                unpaid: rawStats?.unpaid ?? 0
              };
              const isFiltered = filteredStats !== null;
              return (
                <>
                  {isFiltered && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      📊 Τα στατιστικά υπολογίζονται με βάση την επιλεγμένη περίοδο
                    </Alert>
                  )}
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={4}>
                      <Paper sx={{ p: 2, bgcolor: '#e3f2fd', textAlign: 'center' }}>
                        <Typography variant="body2" color="textSecondary">Συνολικός Μισθός</Typography>
                        <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#1976d2' }}>
                          €{stats.totalSalary.toFixed(2)}
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={4}>
                      <Paper sx={{ p: 2, bgcolor: '#e8f5e9', textAlign: 'center' }}>
                        <Typography variant="body2" color="textSecondary">Πληρωμένα</Typography>
                        <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#2e7d32' }}>
                          €{stats.totalPaid.toFixed(2)}
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={4}>
                      <Paper sx={{ p: 2, bgcolor: '#fff3e0', textAlign: 'center' }}>
                        <Typography variant="body2" color="textSecondary">Υπόλοιπο</Typography>
                        <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#f57c00' }}>
                          €{stats.unpaid.toFixed(2)}
                        </Typography>
                      </Paper>
                    </Grid>
                  </Grid>
                </>
              );
            })()}

            {/* Payment History */}
            {paymentHistory.length === 0 ? (
              <Alert severity="info">Δεν υπάρχουν πληρωμές</Alert>
            ) : (() => {
              const groupedHistory = getFilteredAndGroupedHistory();

              if (groupedHistory.length === 0 || (groupedHistory.length === 1 && groupedHistory[0].items.length === 0)) {
                return (
                  <Alert severity="info">
                    Δεν βρέθηκαν πληρωμές με τα επιλεγμένα φίλτρα.
                  </Alert>
                );
              }

              return (
                <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                  {groupedHistory.map((group, groupIndex) => (
                    <Box key={group.groupKey || groupIndex}>
                      {/* Group Header (only if grouped) */}
                      {group.groupLabel && (
                        <Box sx={{
                          bgcolor: '#e3f2fd',
                          p: 1.5,
                          mb: 1,
                          borderRadius: 1,
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                            {group.groupLabel}
                          </Typography>
                          <Chip
                            label={`Σύνολο: €${group.totalAmount.toFixed(2)}`}
                            color="primary"
                            size="small"
                          />
                        </Box>
                      )}

                      {/* Payments in this group */}
                      {group.items.map((payment) => (
                        <Paper
                          key={payment.id}
                          variant="outlined"
                          sx={{ p: 2, mb: 1, position: 'relative', overflow: 'visible' }}
                        >
                          <Box sx={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 1, zIndex: 10 }}>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => onOpenEditPayment(payment)}
                              title="Επεξεργασία"
                              sx={{ bgcolor: 'background.paper', boxShadow: 1 }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => onDeletePayment(payment.id)}
                              title="Διαγραφή"
                              sx={{ bgcolor: 'background.paper', boxShadow: 1 }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                          <Typography variant="body2">
                            {dayjs(payment.payment_date).format('DD/MM/YYYY')} - €{payment.amount} - {payment.payment_method}
                          </Typography>
                        </Paper>
                      ))}
                    </Box>
                  ))}
                </Box>
              );
            })()}
          </Box>
        )}

        {/* Tab 3: Σταθερό Πρόγραμμα (only shown if showFixedSchedule is true) */}
        {showFixedSchedule && tab === 3 && (
          <Box sx={{ pt: 1 }}>
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                Ορίστε τις σταθερές βάρδιες του υπαλλήλου. Αυτές θα δημιουργούνται αυτόματα κάθε εβδομάδα.
              </Typography>
            </Alert>

            <FormControlLabel
              control={
                <Checkbox
                  checked={fixedSchedule.enabled}
                  onChange={(e) => onFixedScheduleChange({ ...fixedSchedule, enabled: e.target.checked })}
                />
              }
              label={<Typography variant="subtitle1" fontWeight="bold">Ενεργοποίηση σταθερού προγράμματος</Typography>}
              sx={{ mb: 2 }}
            />

            {fixedSchedule.enabled && (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle2" gutterBottom sx={{ mb: 2 }}>
                  Επιλέξτε ημέρες και ώρες:
                </Typography>

                {DAYS_FOR_SCHEDULE.map((day) => {
                  const dayData = fixedSchedule.days[day.value] || { enabled: false, start_time: '', end_time: '' };
                  return (
                    <Box
                      key={day.value}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        mb: 1.5,
                        p: 1,
                        borderRadius: 1,
                        bgcolor: dayData.enabled ? 'rgba(25, 118, 210, 0.08)' : 'transparent'
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={dayData.enabled}
                            onChange={(e) => onFixedScheduleChange({
                              ...fixedSchedule,
                              days: {
                                ...fixedSchedule.days,
                                [day.value]: { ...dayData, enabled: e.target.checked }
                              }
                            })}
                          />
                        }
                        label={day.label}
                        sx={{ minWidth: 120 }}
                      />
                      {dayData.enabled && (
                        <>
                          <TextField
                            type="text"
                            label="Έναρξη"
                            size="small"
                            value={dayData.start_time}
                            onChange={(e) => onFixedScheduleChange({
                              ...fixedSchedule,
                              days: {
                                ...fixedSchedule.days,
                                [day.value]: { ...dayData, start_time: e.target.value }
                              }
                            })}
                            placeholder="09:00"
                            InputLabelProps={{ shrink: true }}
                            sx={{ width: 100 }}
                          />
                          <Typography sx={{ color: 'text.secondary' }}>—</Typography>
                          <TextField
                            type="text"
                            label="Λήξη"
                            size="small"
                            value={dayData.end_time}
                            onChange={(e) => onFixedScheduleChange({
                              ...fixedSchedule,
                              days: {
                                ...fixedSchedule.days,
                                [day.value]: { ...dayData, end_time: e.target.value }
                              }
                            })}
                            placeholder="17:00"
                            InputLabelProps={{ shrink: true }}
                            sx={{ width: 100 }}
                          />
                          {/* Preset hours chips */}
                          {presetHours && presetHours.length > 0 && (
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', ml: 1 }}>
                              {presetHours.slice(0, 3).map((preset, idx) => {
                                const [startTime, endTime] = preset.split('-');
                                return (
                                  <Chip
                                    key={idx}
                                    label={preset}
                                    size="small"
                                    onClick={() => onFixedScheduleChange({
                                      ...fixedSchedule,
                                      days: {
                                        ...fixedSchedule.days,
                                        [day.value]: { ...dayData, start_time: startTime, end_time: endTime }
                                      }
                                    })}
                                    sx={{
                                      cursor: 'pointer',
                                      fontSize: '0.7rem',
                                      height: '22px'
                                    }}
                                  />
                                );
                              })}
                            </Box>
                          )}
                        </>
                      )}
                    </Box>
                  );
                })}
              </Paper>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 3 }}>
              <Button onClick={handleClose}>
                Άκυρο
              </Button>
              <Button
                onClick={onSaveFixedSchedule}
                variant="contained"
                disabled={savingFixedSchedule}
              >
                {savingFixedSchedule ? 'Αποθήκευση...' : 'Αποθήκευση'}
              </Button>
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}

